import React, {Fragment, memo} from 'react';
import MUIBox from '@/components/MUI-Components/MUI-Box';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import dynamic from 'next/dynamic';
import Link from 'next/link';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'), {ssr: false});
import parse from 'html-react-parser';
import type {SxProps, Theme} from '@mui/material/styles';
import {colors, spacing} from '@/styles/tokens';

// Descendant selectors below target shared classnames (`.one-step-content-heading`,
// `.one-step-sub-heading`, `.step-image`) that remain global — their `!important`
// is preserved because those classes' own base rules declare colour with
// `!important`, and an sx override without it cannot win that fight.
const sxImgContent = {
    display: 'flex',
    backgroundColor: colors.background.default, // matches original #fff
    padding: `${spacing[28]} ${spacing[20]}`,
    gap: spacing[20],
    float: 'left',
    height: '100%',
    boxShadow: '0 0 8px rgb(0 0 0 / 0.1)', // no exact token match (closest is shadows.sm, 10px blur not 8px); was in globals.scss's `android` partial, folded in here since it targeted this same classname
    '@media (max-width: 430px)': {
        flexDirection: 'column'
    },
    '&:hover': {
        backgroundColor: colors.primary.main,
        '& .one-step-content-heading': {color: `${colors.text.inverse} !important`},
        '& .one-step-sub-heading': {color: `${colors.text.inverse} !important`},
        '& .step-image': {filter: 'brightness(0) invert(1)'},
        '& a': {color: `${colors.text.inverse} !important`}
    }
};

const sxLinkHeadingWrapper = {
    '& :hover': {
        textDecoration: 'underline !important'
    }
};

const sxLinkSubheading = {
    '& a:hover': {
        textDecoration: 'underline !important'
    }
};

// `xSx`/`containerSx` are additive sx escape hatches alongside the existing `className`/
// `containerClass` string passthroughs — they let per-page callers override this component's
// item-wrapper/grid-container styling without a new global classname. Existing `className`/
// `containerClass` consumers are unaffected; migrating them off global CSS is separate,
// long-tail work (Initiative 6), not done here.

const OneStepSolution = (props: any) => {
    const {OneStepData, sectionhead, subhead, className, containerClass, xSx, containerSx} = props;
    return (
        <MUIBox className="one-step-component-wrapper">
            <MUIBox className="spectrum-header-wrapper solutions">
                <MUITypography className="spectrum-heading color-black our-service-main-heading " component="h2">
                    {sectionhead}
                </MUITypography>
                <MUIBox className={subhead !== undefined ? 'divder-wrapper' : ''}>
                    <hr className="divider-line" />
                </MUIBox>
                {subhead ? (
                    <MUIBox className="sub-heading-box">
                        <MUITypography className="spectrum-sub-heading color-black">{subhead ? parse(subhead) : null}</MUITypography>
                    </MUIBox>
                ) : null}
            </MUIBox>

            <MUIGrid container spacing={3} className={containerClass} sx={containerSx}>
                {OneStepData?.map((data: any, index: number) => (
                    <Fragment key={index + 1}>
                        <MUIGrid item xs={12} sm={6} md={6} lg={6}>
                            <MUIBox className={className} sx={[sxImgContent, xSx] as SxProps<Theme>}>
                                <MUIBox>
                                    <DisplayImage src={data.icons} alt={data.heading} priority={false} className="step-image" />
                                </MUIBox>
                                <MUIBox>
                                    {data.redirect !== '' ? (
                                        <MUIBox
                                            component={Link}
                                            href={data && data?.redirect}
                                            target="_blank"
                                            className="services_link menu-links"
                                            sx={sxLinkHeadingWrapper}
                                        >
                                            <MUITypography className="one-step-content-heading" component="h3">
                                                {data.heading}
                                            </MUITypography>
                                        </MUIBox>
                                    ) : (
                                        <MUITypography className="one-step-content-heading" component="h3">
                                            {data.heading}
                                        </MUITypography>
                                    )}

                                    <MUIBox component="p" className="data_links one-step-sub-heading" sx={sxLinkSubheading}>
                                        {data?.content ? parse(data.content) : ''}
                                    </MUIBox>
                                </MUIBox>
                            </MUIBox>
                        </MUIGrid>
                    </Fragment>
                ))}
            </MUIGrid>
        </MUIBox>
    );
};
export default memo(OneStepSolution);
